import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Disclaimer from '../components/Disclaimer';
import './LoginPage.css';

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState(null);
  const [blockType, setBlockType] = useState('');
  const navigate = useNavigate();

  // CAPTCHA 요청 함수
  const requestCaptcha = async () => {
    try {
      const response = await axios.post('/api/auth/captcha', {
        email: email.toLowerCase().trim()
      });
      
      if (response.data.success) {
        setCaptchaToken(response.data.captcha.token);
        setCaptchaQuestion(response.data.captcha.question);
        setCaptchaRequired(true);
      }
    } catch (err) {
      console.error('CAPTCHA request error:', err);
    }
  };

  // 차단 시간 포맷팅 함수
  const formatBlockedTime = (timestamp) => {
    if (!timestamp) return '';
    const now = Date.now();
    const remaining = Math.max(0, timestamp - now);
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // 차단 상태 확인 함수
  const checkBlockStatus = () => {
    if (isBlocked && blockedUntil) {
      const now = Date.now();
      if (now >= blockedUntil) {
        setIsBlocked(false);
        setBlockedUntil(null);
        setBlockType('');
      }
    }
  };

  // 차단 상태 체크를 위한 타이머
  useEffect(() => {
    if (isBlocked && blockedUntil) {
      const timer = setInterval(checkBlockStatus, 1000);
      return () => clearInterval(timer);
    }
  }, [isBlocked, blockedUntil]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const loginData = {
        email: email.toLowerCase().trim(),
        password,
      };

      // CAPTCHA가 필요한 경우 토큰과 답변 추가
      if (captchaRequired && captchaToken) {
        loginData.captchaToken = captchaToken;
        loginData.captchaAnswer = captchaAnswer;
      }

      const response = await axios.post('/api/auth/login', loginData);

      if (response.data.success) {
        onLogin(response.data.user, response.data.token);
      }
    } catch (err) {
      console.error('Login error:', err);
      
      const response = err.response?.data;
      
      // 차단된 경우 처리
      if (response?.type === 'ip_blocked' || response?.type === 'account_blocked') {
        setIsBlocked(true);
        setBlockedUntil(response.blockedUntil);
        setBlockType(response.type);
        setError(response.message);
        return;
      }
      
      // CAPTCHA 필요한 경우 처리
      if (response?.type === 'captcha_required') {
        setCaptchaRequired(true);
        await requestCaptcha();
        setError(response.message);
        return;
      }
      
      // CAPTCHA 실패한 경우 처리
      if (response?.type === 'captcha_failed') {
        setError(response.message);
        await requestCaptcha(); // 새로운 CAPTCHA 요청
        return;
      }
      
      // 네트워크 연결 문제 감지
      if (err.code === 'ERR_NETWORK' || err.message.includes('Network Error')) {
        setError('Network connection failed. Please check your internet connection.');
      } else if (err.response?.status === 404) {
        setError('Unable to connect to server. Please try again later.');
      } else if (err.response?.status === 500) {
        setError('Server error occurred. Please try again later.');
      } else if (response?.message) {
        setError(response.message);
      } else {
        setError('Login failed. Please check your email and password.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = () => {
    navigate('/request-password-reset');
  };

  return (
    <div className="login-outer">
      <div className="login-page">
        <div className="login-container">
          {/* Header */}
          <div className="login-header">
            <div className="login-logo" style={{ background: 'none', boxShadow: 'none', color: 'inherit', cursor: 'pointer' }} onClick={() => window.location.reload()}>
              <img src="/logo-black.png" alt="VegaX Logo" style={{ width: 104, height: 104, display: 'block', objectFit: 'contain' }} />
            </div>
          </div>

          {/* Login Form */}
          <div className="login-form-container">
            <form onSubmit={handleSubmit} className="login-form">
              {error && (
                <div className={`error-alert ${isBlocked ? 'blocked-alert' : ''}`}>
                  <span>⚠️</span>
                  {error}
                  {isBlocked && blockedUntil && (
                    <div className="blocked-info">
                      <div className="blocked-type">
                        {blockType === 'ip_blocked' ? 'IP 차단' : '계정 차단'}
                      </div>
                      <div className="blocked-timer">
                        남은 시간: {formatBlockedTime(blockedUntil)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="form-input"
                  required
                  disabled={isBlocked}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-container">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="form-input"
                    required
                    disabled={isBlocked}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    disabled={isBlocked}
                  >
                    {showPassword ? (
                      // 눈+빗금(숨김) SVG
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 2L18 18" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M10 4C5 4 2 10 2 10C2 10 4.5 15 10 15C12.1 15 13.9 14.2 15.2 13.1M17.5 10.5C17.5 10.5 15 4 10 4" stroke="#888" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="10" cy="10" r="3" stroke="#888" strokeWidth="2"/>
                      </svg>
                    ) : (
                      // 눈(보임) SVG
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2 10C2 10 5 4 10 4C15 4 18 10 18 10C18 10 15 16 10 16C5 16 2 10 2 10Z" stroke="#888" strokeWidth="2"/>
                        <circle cx="10" cy="10" r="3" stroke="#888" strokeWidth="2"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* CAPTCHA 섹션 */}
              {captchaRequired && (
                <div className="form-group captcha-group">
                  <label htmlFor="captcha">Security Challenge</label>
                  <div className="captcha-container">
                    <div className="captcha-question">
                      <span className="captcha-icon">🔒</span>
                      <span className="captcha-text">{captchaQuestion}</span>
                    </div>
                    <input
                      id="captcha"
                      type="text"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      placeholder="Enter your answer"
                      className="form-input captcha-input"
                      required
                      disabled={isBlocked}
                    />
                    <button
                      type="button"
                      className="captcha-refresh"
                      onClick={requestCaptcha}
                      disabled={isBlocked}
                      title="새로운 보안 질문 요청"
                    >
                      🔄
                    </button>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="login-button"
                disabled={isLoading || isBlocked}
              >
                {isLoading ? 'Logging in...' : isBlocked ? 'Access Blocked' : 'Login'}
              </button>
              <button
                type="button"
                className="reset-password-button"
                onClick={handleResetPassword}
                style={{ marginTop: 12 }}
                disabled={isBlocked}
              >
                Reset Password
              </button>
            </form>
          </div>
          <div className="login-reset-info-wide">
            <div style={{ fontWeight: 'bold' }}>First time logging in to the new interface?</div>
            <div>Please reset your password using your original email address.</div>
            <div>If you need help, <span className="contact-link" onClick={() => navigate('/contact')} style={{ color: 'var(--vegax-accent)', textDecoration: 'underline', cursor: 'pointer' }}>contact us</span>.</div>
          </div>
        </div>
      </div>
      
      {/* Footer Section */}
      <div className="login-footer-section">
        <Disclaimer />
        <div className="login-footer">
          <div className="login-footer-content">
            <p>&copy; {new Date().getFullYear()} VegaX Holdings Inc. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 